import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Star, MapPin, Award, Play, Search, ChevronRight } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import WhatsAppButton from '../components/WhatsAppButton';
import Breadcrumbs from '../components/Breadcrumbs';
import { serviceCategories } from '../data/serviceCategories';
import { serviceProviders } from '../data/serviceProviders';

const ServicesPage = () => {
  const { country } = useParams();
  const navigate = useNavigate();
  const [searchCity, setSearchCity] = useState('');
  const [searchState, setSearchState] = useState('');
  const [topProviders, setTopProviders] = useState([]);
  const [filteredProviders, setFilteredProviders] = useState([]);

  useEffect(() => {
    // Top 10 prestadores
    const sorted = [...serviceProviders]
      .sort((a, b) => (b.average_rating * 100 + b.total_contracts) - (a.average_rating * 100 + a.total_contracts))
      .slice(0, 10);
    
    setTopProviders(sorted);
    setFilteredProviders(serviceProviders);
  }, []);

  const handleSearch = () => {
    let filtered = [...serviceProviders];
    
    if (searchCity) {
      filtered = filtered.filter(p => 
        p.city.toLowerCase().includes(searchCity.toLowerCase())
      );
    }
    
    if (searchState) {
      filtered = filtered.filter(p => 
        p.state.toLowerCase() === searchState.toLowerCase()
      );
    }
    
    // Ordenar por rating
    filtered.sort((a, b) => (b.average_rating * 100 + b.reviews_count) - (a.average_rating * 100 + a.reviews_count));
    
    setFilteredProviders(filtered);
  };

  const handleProviderClick = (providerId) => {
    navigate(`/${country}/services/${providerId}`);
  };

  const handleCategoryClick = (categorySlug) => {
    navigate(`/${country}/services/category/${categorySlug}`);
  };

  return (
    <div className="min-h-screen bg-aug-cream">
      <Header />
      <WhatsAppButton />

      <div className="pt-24 pb-16">
        <div className="max-w-[1400px] mx-auto px-xl">
          <Breadcrumbs
            items={[
              { label: 'Home', path: `/${country}` },
              { label: 'Prestadores de Serviços', path: `/${country}/services` }
            ]}
          />

          {/* Hero */}
          <div className="mb-12 text-center">
            <h1 className="text-5xl font-bold mb-4" style={{ color: '#1B5E20' }}>
              Prestadores de Serviços Especializados
            </h1>
            <p className="text-xl text-aug-gray-medium max-w-3xl mx-auto">
              Encontre profissionais qualificados próximos a você. Avaliações reais e vídeos demonstrativos para escolher com confiança.
            </p>
          </div>

          {/* Busca por Localização */}
          <section className="mb-16">
            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <div className="flex items-center gap-3 mb-6">
                <MapPin className="w-6 h-6" style={{ color: '#1B5E20' }} />
                <h2 className="text-2xl font-bold text-aug-gray-dark">
                  Buscar por Localização
                </h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <input
                  type="text"
                  placeholder="Cidade (ex: São Paulo)"
                  value={searchCity}
                  onChange={(e) => setSearchCity(e.target.value)}
                  className="px-4 py-3 border border-aug-gray-light rounded-lg focus:outline-none focus:ring-2 focus:ring-aug-blue"
                />
                <input
                  type="text"
                  placeholder="Estado (ex: SP)"
                  value={searchState}
                  onChange={(e) => setSearchState(e.target.value.toUpperCase())}
                  maxLength={2}
                  className="px-4 py-3 border border-aug-gray-light rounded-lg focus:outline-none focus:ring-2 focus:ring-aug-blue"
                />
                <button
                  onClick={handleSearch}
                  className="flex items-center justify-center gap-2 px-6 py-3 text-white rounded-lg font-semibold transition-colors"
                  style={{ backgroundColor: '#1B5E20' }}
                >
                  <Search className="w-5 h-5" />
                  Buscar
                </button>
              </div>

              {/* Resultados da Busca */}
              {(searchCity || searchState) && (
                <div className="mt-6 pt-6 border-t border-aug-gray-light">
                  <p className="text-sm text-aug-gray-medium mb-4">
                    {filteredProviders.length} profissionais encontrados
                    {searchCity && ` em ${searchCity}`}
                    {searchState && ` - ${searchState}`}
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {filteredProviders.slice(0, 4).map((provider) => (
                      <ProviderMiniCard
                        key={provider.id}
                        provider={provider}
                        onClick={() => handleProviderClick(provider.id)}
                      />
                    ))}
                  </div>
                  {filteredProviders.length > 4 && (
                    <button
                      onClick={() => {/* scroll ou expandir */}}
                      className="mt-4 text-aug-blue font-medium hover:underline"
                    >
                      Ver todos os {filteredProviders.length} resultados →
                    </button>
                  )}
                </div>
              )}
            </div>
          </section>

          {/* Top 10 Prestadores */}
          <section className="mb-16">
            <div className="flex items-center gap-3 mb-6">
              <Award className="w-7 h-7 text-yellow-500" />
              <div>
                <h2 className="text-3xl font-bold text-aug-gray-dark">
                  Top 10 Prestadores
                </h2>
                <p className="text-aug-gray-medium">Os mais bem avaliados e contratados</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              {topProviders.map((provider, index) => (
                <div
                  key={provider.id}
                  onClick={() => handleProviderClick(provider.id)}
                  className="bg-white rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1 cursor-pointer group relative"
                >
                  {/* Ranking Badge */}
                  <div className="absolute top-2 left-2 w-8 h-8 bg-yellow-500 text-white rounded-full flex items-center justify-center font-bold text-sm z-10">
                    {index + 1}
                  </div>

                  {/* Profile Image */}
                  <div className="relative h-48 overflow-hidden bg-gray-100">
                    <img
                      src={provider.profile_image}
                      alt={provider.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                    {provider.videos.length > 0 && (
                      <div className="absolute bottom-2 right-2 bg-black bg-opacity-70 text-white px-2 py-1 rounded text-xs flex items-center gap-1">
                        <Play className="w-3 h-3" />
                        {provider.videos.length}
                      </div>
                    )}
                  </div>

                  {/* Info */}
                  <div className="p-3">
                    <h3 className="text-sm font-semibold text-aug-gray-dark line-clamp-2 mb-1 min-h-[2.5rem]">
                      {provider.name}
                    </h3>
                    
                    <p className="text-xs text-aug-gray-medium mb-2">
                      {provider.category_name}
                    </p>

                    {/* Rating */}
                    <div className="flex items-center gap-1 mb-2">
                      <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                      <span className="text-sm font-bold">{provider.average_rating}</span>
                      <span className="text-xs text-aug-gray-medium">({provider.reviews_count})</span>
                    </div>

                    {/* Location */}
                    <div className="flex items-center gap-1 text-xs text-aug-gray-medium">
                      <MapPin className="w-3 h-3" />
                      <span>{provider.city} - {provider.state}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Categorias de Serviço */}
          <section className="mb-16">
            <div className="mb-6">
              <h2 className="text-3xl font-bold text-aug-gray-dark mb-2">
                Categorias de Serviço
              </h2>
              <p className="text-aug-gray-medium">Escolha a categoria do profissional que você precisa</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {serviceCategories.map((category) => (
                <div
                  key={category.id}
                  onClick={() => handleCategoryClick(category.slug)}
                  className="bg-white rounded-xl p-6 shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1 cursor-pointer group"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-16 h-16 bg-aug-blue-light rounded-lg flex items-center justify-center text-3xl group-hover:bg-aug-blue transition-colors">
                      {category.icon}
                    </div>
                    <div className="flex items-center gap-1">
                      <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                      <span className="text-sm font-bold">{category.average_rating}</span>
                    </div>
                  </div>

                  <h3 className="text-xl font-bold text-aug-gray-dark mb-2 group-hover:text-aug-blue transition-colors">
                    {category.name}
                  </h3>
                  <p className="text-sm text-aug-gray-medium mb-3 line-clamp-2">
                    {category.description}
                  </p>

                  <div className="flex items-center justify-between text-sm">
                    <span className="text-aug-gray-medium">
                      {category.providers_count} profissionais
                    </span>
                    <div className="flex items-center gap-1 text-aug-blue group-hover:translate-x-1 transition-transform">
                      <span className="font-medium">Ver profissionais</span>
                      <ChevronRight className="w-4 h-4" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* CTA */}
          <section className="bg-gradient-to-r from-aug-blue to-aug-green text-white rounded-2xl p-12 text-center">
            <h2 className="text-3xl font-bold mb-4">
              Não encontrou o que precisa?
            </h2>
            <p className="text-xl mb-6 max-w-2xl mx-auto opacity-90">
              Entre em contato conosco e ajudaremos você a encontrar o profissional ideal
            </p>
            <button
              onClick={() => navigate(`/${country}/crm`)}
              className="bg-white text-aug-blue px-8 py-4 rounded-xl font-semibold text-lg hover:bg-aug-cream transition-all duration-300 shadow-lg hover:shadow-xl"
            >
              Solicitar Ajuda
            </button>
          </section>
        </div>
      </div>

      <Footer />
    </div>
  );
};

// Mini Card Component
const ProviderMiniCard = ({ provider, onClick }) => (
  <div
    onClick={onClick}
    className="flex gap-4 p-4 bg-aug-cream rounded-lg hover:bg-white transition-colors cursor-pointer"
  >
    <img
      src={provider.profile_image}
      alt={provider.name}
      className="w-16 h-16 rounded-lg object-cover"
    />
    <div className="flex-1">
      <h4 className="font-semibold text-aug-gray-dark line-clamp-1">{provider.name}</h4>
      <p className="text-xs text-aug-gray-medium">{provider.category_name}</p>
      <div className="flex items-center gap-2 mt-1">
        <div className="flex items-center gap-1">
          <Star className="w-3 h-3 fill-yellow-400 text-yellow-400" />
          <span className="text-sm font-bold">{provider.average_rating}</span>
        </div>
        <span className="text-xs text-aug-gray-medium">•</span>
        <div className="flex items-center gap-1 text-xs text-aug-gray-medium">
          <MapPin className="w-3 h-3" />
          {provider.city}
        </div>
      </div>
    </div>
  </div>
);

export default ServicesPage;
